import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "@/shared/components/ui/Button"
import type { FarmUserResponse, FarmUserRole } from "@/shared/types/api"

// Schema for creating a new team member
const createSchema = z.object({
  username: z
    .string()
    .min(3, "Username must be at least 3 characters")
    .max(50, "Username must be less than 50 characters")
    .regex(
      /^[a-z0-9][a-z0-9_.-]{2,49}$/,
      "Username must start with a letter or number, and contain only lowercase letters, numbers, underscores, dots, or hyphens"
    ),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(72, "Password must be less than 72 characters"),
  displayName: z.string().max(100, "Display name must be less than 100 characters").optional(),
  role: z.enum(["admin", "worker"] as const),
  email: z.string().email("Invalid email format").max(255).optional().or(z.literal("")),
  mobileNumber: z.string().max(20, "Mobile number must be less than 20 characters").optional(),
})

// Schema for updating an existing team member (no password/username)
const updateSchema = z.object({
  displayName: z.string().max(100, "Display name must be less than 100 characters").optional(),
  role: z.enum(["admin", "worker"] as const).optional(),
  status: z.enum(["active", "inactive", "suspended"] as const).optional(),
  email: z.string().email("Invalid email format").max(255).optional().or(z.literal("")),
  mobileNumber: z.string().max(20, "Mobile number must be less than 20 characters").optional(),
})

type CreateFormData = z.infer<typeof createSchema>
type UpdateFormData = z.infer<typeof updateSchema>

interface TeamMemberFormProps {
  teamMember?: FarmUserResponse | null
  onSubmit: (data: CreateFormData | UpdateFormData) => Promise<void>
  onCancel: () => void
  isLoading: boolean
}

export function TeamMemberForm({ teamMember, onSubmit, onCancel, isLoading }: TeamMemberFormProps) {
  const isEdit = !!teamMember

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateFormData | UpdateFormData>({
    resolver: zodResolver(isEdit ? updateSchema : createSchema),
    defaultValues: isEdit
      ? {
          displayName: teamMember.displayName || "",
          role: teamMember.role as FarmUserRole,
          status: teamMember.status,
          email: teamMember.email || "",
          mobileNumber: teamMember.mobileNumber || "",
        }
      : {
          username: "",
          password: "",
          displayName: "",
          role: "worker" as FarmUserRole,
          email: "",
          mobileNumber: "",
        },
  })

  const onFormSubmit = handleSubmit(async (data) => {
    // Filter out empty strings for optional fields
    const cleanedData = {
      ...data,
      email: data.email || undefined,
      mobileNumber: data.mobileNumber || undefined,
      displayName: data.displayName || undefined,
    }
    await onSubmit(cleanedData)
  })

  return (
    <form onSubmit={onFormSubmit} className="space-y-4">
      {/* Username (only for create) */}
      {!isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Username <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register("username")}
            className="w-full px-3 py-2 border border-slate-200/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            placeholder="e.g., emp_1234 or john_smith"
          />
          {'username' in errors && errors.username && (
            <p className="mt-1 text-sm text-red-500">{errors.username.message}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            This will be used for login. Must be lowercase with letters, numbers, underscores, dots, or hyphens.
          </p>
        </div>
      )}

      {/* Display Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
        <input
          type="text"
          {...register("displayName")}
          className="w-full px-3 py-2 border border-slate-200/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          placeholder="e.g., John Smith"
        />
        {errors.displayName && (
          <p className="mt-1 text-sm text-red-500">{errors.displayName.message}</p>
        )}
      </div>

      {/* Password (only for create) */}
      {!isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            {...register("password")}
            className="w-full px-3 py-2 border border-slate-200/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            placeholder="Minimum 8 characters"
          />
          {'password' in errors && errors.password && (
            <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
          )}
        </div>
      )}

      {/* Role */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
        <select
          {...register("role")}
          className="w-full px-3 py-2 border border-slate-200/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
        >
          <option value="worker">Worker</option>
          <option value="admin">Admin</option>
        </select>
        {errors.role && <p className="mt-1 text-sm text-red-500">{errors.role.message}</p>}
        <p className="mt-1 text-xs text-gray-500">
          Admins can manage team members, create ponds, and delete checks. Workers can only create and view checks.
        </p>
      </div>

      {/* Status (only for edit) */}
      {isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            {...register("status")}
            className="w-full px-3 py-2 border border-slate-200/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          {'status' in errors && errors.status && <p className="mt-1 text-sm text-red-500">{errors.status.message}</p>}
        </div>
      )}

      {/* Email (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
        <input
          type="email"
          {...register("email")}
          className="w-full px-3 py-2 border border-slate-200/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          placeholder="worker@example.com"
        />
        {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
      </div>

      {/* Mobile Number (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Mobile Number (optional)
        </label>
        <input
          type="tel"
          {...register("mobileNumber")}
          className="w-full px-3 py-2 border border-slate-200/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          placeholder="+1234567890"
        />
        {errors.mobileNumber && (
          <p className="mt-1 text-sm text-red-500">{errors.mobileNumber.message}</p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <Button type="submit" disabled={isLoading} className="flex-1">
          {isLoading ? "Saving..." : isEdit ? "Update Team Member" : "Add Team Member"}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
